// src/data/projectHubClient.js
//
// PMO Slice A — Projects hub read boundary.
//   POST /api/projects/hub/weekly             -> { weekDate, posted, eligible, projectCount }
//   POST /api/projects/hub/financials         -> { rows, totals, currency, sourceCaption, scope, snapshotId }
//   POST /api/projects/hub/financials/export  -> xlsx Blob for a previously read snapshot
//
// Read-only. Server errors surface verbatim (errorFromResponse uses body.error).

import { NetworkError, ValidationError, errorFromResponse } from "./errors.js";
import { invalidateProjectWeekly } from "../services/projectWeeklyCache.js";

const BASE = "/api/projects/hub";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const HUB_FINANCIAL_FIELDS = Object.freeze([
  "budget", "spendToDate", "forecast", "variance",
]);

function normalizeIds(projectIds) {
  if (!Array.isArray(projectIds)) throw new ValidationError("projectIds must be an array");
  const ids = [];
  for (const id of projectIds) {
    if (typeof id !== "string" || !UUID_RE.test(id)) {
      throw new ValidationError("projectIds must contain only project UUIDs");
    }
    if (!ids.includes(id)) ids.push(id);
  }
  return ids;
}

async function post(path, body, { signal } = {}) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new NetworkError(`Network error: ${err?.message || err}`);
  }
  if (!res.ok) throw await errorFromResponse(res);
  return res;
}

export async function getHubWeekly(projectIds, opts) {
  const res = await post("/weekly", { projectIds: normalizeIds(projectIds), ...(opts?.weekDate ? { weekDate: opts.weekDate } : {}) }, opts);
  return res.json();
}

export async function demoWeekAction(mode, opts) {
  const res = await post("/demo-week", { mode, weekDate: "2026-10-12" }, opts);
  const data = await res.json();
  invalidateProjectWeekly();
  return data;
}

export async function getHubFinancials(projectIds, opts) {
  const res = await post("/financials", { projectIds: normalizeIds(projectIds) }, opts);
  return res.json();
}

export async function exportHubFinancials(projectIds, snapshotId, opts) {
  if (typeof snapshotId !== "string" || snapshotId.length === 0) {
    throw new ValidationError("snapshotId is required");
  }
  const res = await post("/financials/export", { projectIds: normalizeIds(projectIds), snapshotId }, opts);
  return res.blob();
}

/** Stable key for a project ID selection (order-insensitive). */
export function projectIdsKey(projectIds) {
  return [...new Set(projectIds || [])].sort().join(",");
}
